import { Controller } from "@hotwired/stimulus"

/**
 * ViewingBookingController - Manages the multi-step viewing booking wizard
 *
 * Steps:
 *   1. Time - Select date and time slot
 *   2. Details - Enter personal information
 *   3. Confirm - Review and submit booking
 *
 * Features:
 *   - Smooth step transitions
 *   - Form validation per step
 *   - State persistence during navigation
 *   - AJAX slot loading
 *   - Responsive design (desktop/mobile)
 */
export default class extends Controller {
  static targets = [
    // Step containers
    "stepTime",
    "stepDetails",
    "stepConfirm",
    // Step indicators (desktop)
    "indicatorTime",
    "indicatorDetails",
    "indicatorConfirm",
    // Step connector lines
    "line1",
    "line2",
    // Date picker
    "dateScroller",
    "dateButton",
    "calendarModal",
    "monthLabel",
    // Mobile calendar modal
    "mobileCalendarContainer",
    "calendarMonthTitle",
    "calendarSelectedDateLabel",
    "calendarTimeSlotsContainer",
    "calendarPrevButton",
    "calendarNextButton",
    // Desktop calendar modal
    "desktopCalendarContainer",
    "desktopCalendarDateLabel",
    "desktopCalendarTimeSlotsContainer",
    // Inline calendar (mobile)
    "collapsedDateView",
    "expandedCalendarView",
    "inlineCalendarContainer",
    "inlineCalendarMonthTitle",
    "inlineCalendarDateLabel",
    "inlineCalendarTimeSlotsContainer",
    "inlineCalendarPrevButton",
    "inlineCalendarNextButton",
    // Time slots
    "slotsContainer",
    "noSlots",
    // Form fields
    "form",
    "scheduledAtInput",
    "firstNameInput",
    "lastNameInput",
    "emailInput",
    "phoneInput",
    // Summary panel (desktop)
    "summaryDate",
    "summaryTime",
    "summaryButtonContainer",
    "continueButtonText",
    // Mobile bottom bar
    "mobileBar",
    "mobileBarSpacer",
    "mobileBarButton",
    "mobileBarText",
    "mobileBarDate",
    "mobileBarTime",
    "continueButton",
    // Error display
    "stepError"
  ]

  static values = {
    slotsUrl: String,
    selectedDate: String,
    selectedTime: String,
    currentStep: { type: Number, default: 1 },
    propertySlug: String,
    datesWithAvailability: Array,
    calendarMonth: String,  // Current month displayed in mobile calendar (YYYY-MM)
    agencySubdomain: String // Used to scope sessionStorage
  }

  connect() {
    this.formData = {
      scheduled_at: null,
      first_name: "",
      last_name: "",
      email: "",
      phone: ""
    }

    // Initialize to step 1
    this.currentStepValue = 1
    this.showStep(1)
    this.updateIndicators()

    // If we have a pre-selected date, load its slots
    if (this.selectedDateValue) {
      this.loadSlotsForDate(this.selectedDateValue)
    }

    // Restore saved viewer details from localStorage
    this.restoreViewerDetails()
  }

  // ==========================================
  // Local Storage - Save viewer details with 24-hour expiry
  // ==========================================

  get storageKey() {
    return "letadmin_viewer_details"
  }

  get expiryHours() {
    return 24
  }

  saveViewerDetails() {
    const details = {
      first_name: this.hasFirstNameInputTarget ? this.firstNameInputTarget.value : "",
      last_name: this.hasLastNameInputTarget ? this.lastNameInputTarget.value : "",
      email: this.hasEmailInputTarget ? this.emailInputTarget.value : "",
      phone: this.hasPhoneInputTarget ? this.phoneInputTarget.value : "",
      saved_at: Date.now()
    }

    // Only save if at least one field has a value
    if (details.first_name || details.last_name || details.email || details.phone) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(details))
      } catch (e) {
        // localStorage might not be available (private browsing, etc.)
        console.warn("Unable to save viewer details to localStorage:", e)
      }
    }
  }

  restoreViewerDetails() {
    try {
      const saved = localStorage.getItem(this.storageKey)
      if (!saved) return

      const details = JSON.parse(saved)

      // Check if data has expired (24 hours)
      if (details.saved_at) {
        const ageHours = (Date.now() - details.saved_at) / (1000 * 60 * 60)
        if (ageHours > this.expiryHours) {
          this.clearSavedViewerDetails()
          return
        }
      }

      // Pre-fill form fields if they exist and are empty
      if (this.hasFirstNameInputTarget && !this.firstNameInputTarget.value && details.first_name) {
        this.firstNameInputTarget.value = details.first_name
      }
      if (this.hasLastNameInputTarget && !this.lastNameInputTarget.value && details.last_name) {
        this.lastNameInputTarget.value = details.last_name
      }
      if (this.hasEmailInputTarget && !this.emailInputTarget.value && details.email) {
        this.emailInputTarget.value = details.email
      }
      if (this.hasPhoneInputTarget && !this.phoneInputTarget.value && details.phone) {
        this.phoneInputTarget.value = details.phone
      }
    } catch (e) {
      console.warn("Unable to restore viewer details from localStorage:", e)
    }
  }

  clearSavedViewerDetails() {
    try {
      localStorage.removeItem(this.storageKey)
    } catch (e) {
      // Ignore errors
    }
  }

  // ==========================================
  // Step Navigation
  // ==========================================

  goToStep(step) {
    // Validate current step before proceeding forward
    if (step > this.currentStepValue && !this.validateCurrentStep()) {
      return
    }

    this.currentStepValue = step
    this.showStep(step)
    this.updateIndicators()
    this.updateButtonText()
    this.scrollToTop()
  }

  nextStep() {
    if (this.currentStepValue < 3) {
      this.goToStep(this.currentStepValue + 1)
    }
  }

  previousStep() {
    if (this.currentStepValue > 1) {
      this.clearErrors()
      this.goToStep(this.currentStepValue - 1)
    }
  }

  goToTime() {
    this.clearErrors()
    this.goToStepWithoutValidation(1)
  }

  goToDetails() {
    // Only allow going to details if time is selected, or if going back from step 3
    if (this.currentStepValue > 2 || this.selectedTimeValue) {
      this.clearErrors()
      this.goToStepWithoutValidation(2)
    }
  }

  goToConfirm() {
    this.goToStep(3)
  }

  goToStepWithoutValidation(step) {
    this.currentStepValue = step
    this.showStep(step)
    this.updateIndicators()
    this.updateButtonText()
    this.scrollToTop()
  }

  showStep(step) {
    // Hide all steps
    if (this.hasStepTimeTarget) this.stepTimeTarget.classList.add("hidden")
    if (this.hasStepDetailsTarget) this.stepDetailsTarget.classList.add("hidden")
    if (this.hasStepConfirmTarget) this.stepConfirmTarget.classList.add("hidden")

    // Show current step with fade-in animation
    const stepTargets = {
      1: this.stepTimeTarget,
      2: this.stepDetailsTarget,
      3: this.stepConfirmTarget
    }

    const currentTarget = stepTargets[step]
    if (currentTarget) {
      currentTarget.classList.remove("hidden")
      currentTarget.classList.add("animate-fade-in")
    }
  }

  updateIndicators() {
    const step = this.currentStepValue

    // Time indicators (multiple instances across step partials)
    this.indicatorTimeTargets.forEach(el => {
      this.updateIndicator(el, step === 1 ? "current" : step > 1 ? "completed" : "upcoming")
    })

    // Details indicators - steps 2 AND 3 are both "Details" phase
    this.indicatorDetailsTargets.forEach(el => {
      this.updateIndicator(el, (step === 2 || step === 3) ? "current" : step > 3 ? "completed" : "upcoming")
    })

    // Done indicator - only completed after form is submitted (step > 3)
    this.indicatorConfirmTargets.forEach(el => {
      this.updateIndicator(el, step > 3 ? "completed" : "upcoming")
    })

    // Update connector lines
    this.line1Targets.forEach(el => {
      el.classList.toggle("line-completed", step > 1)
    })
    this.line2Targets.forEach(el => {
      el.classList.toggle("line-completed", step > 2)
    })
  }

  updateIndicator(element, state) {
    element.classList.remove("step-current", "step-completed", "step-upcoming")
    element.classList.add(`step-${state}`)
  }

  showContinueButtons() {
    // Show desktop summary button container
    if (this.hasSummaryButtonContainerTarget) {
      this.summaryButtonContainerTarget.classList.remove("hidden")
    }

    // Show mobile bottom bar and spacer
    if (this.hasMobileBarTarget) {
      this.mobileBarTarget.classList.remove("hidden")
    }
    if (this.hasMobileBarSpacerTarget) {
      this.mobileBarSpacerTarget.classList.remove("hidden")
    }

    // Update button text based on current step
    this.updateButtonText()
  }

  hideContinueButtons() {
    // Hide desktop summary button container
    if (this.hasSummaryButtonContainerTarget) {
      this.summaryButtonContainerTarget.classList.add("hidden")
    }

    // Hide mobile bottom bar and spacer
    if (this.hasMobileBarTarget) {
      this.mobileBarTarget.classList.add("hidden")
    }
    if (this.hasMobileBarSpacerTarget) {
      this.mobileBarSpacerTarget.classList.add("hidden")
    }
  }

  updateButtonText() {
    const step = this.currentStepValue
    const text = step === 3 ? "Book Viewing" : "Continue"

    if (this.hasContinueButtonTextTarget) {
      this.continueButtonTextTarget.textContent = text
    }
    if (this.hasMobileBarTextTarget) {
      this.mobileBarTextTarget.textContent = text
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // ==========================================
  // Step Validation
  // ==========================================

  validateCurrentStep() {
    this.clearErrors()

    switch (this.currentStepValue) {
      case 1:
        return this.validateTimeStep()
      case 2:
        return this.validateDetailsStep()
      case 3:
        return true // Confirmation step doesn't need validation
      default:
        return true
    }
  }

  validateTimeStep() {
    if (!this.selectedDateValue || !this.selectedTimeValue) {
      this.showError("Please select a date and time for your viewing")
      return false
    }
    return true
  }

  validateDetailsStep() {
    const errors = []

    if (this.hasFirstNameInputTarget && !this.firstNameInputTarget.value.trim()) {
      errors.push("First name is required")
      this.firstNameInputTarget.classList.add("border-red-500")
    }

    if (this.hasLastNameInputTarget && !this.lastNameInputTarget.value.trim()) {
      errors.push("Last name is required")
      this.lastNameInputTarget.classList.add("border-red-500")
    }

    if (this.hasEmailInputTarget) {
      const email = this.emailInputTarget.value.trim()
      if (!email) {
        errors.push("Email is required")
        this.emailInputTarget.classList.add("border-red-500")
      } else if (!this.isValidEmail(email)) {
        errors.push("Please enter a valid email address")
        this.emailInputTarget.classList.add("border-red-500")
      }
    }

    if (this.hasPhoneInputTarget && !this.phoneInputTarget.value.trim()) {
      errors.push("Phone number is required")
      this.phoneInputTarget.classList.add("border-red-500")
    }

    if (errors.length > 0) {
      this.showError(errors[0])
      return false
    }

    return true
  }

  canProceedFromCurrentStep() {
    switch (this.currentStepValue) {
      case 1:
        return !!this.selectedTimeValue
      case 2:
        return this.hasRequiredFields()
      default:
        return true
    }
  }

  hasRequiredFields() {
    if (!this.hasFirstNameInputTarget || !this.hasLastNameInputTarget ||
        !this.hasEmailInputTarget || !this.hasPhoneInputTarget) {
      return false
    }

    return this.firstNameInputTarget.value.trim() &&
           this.lastNameInputTarget.value.trim() &&
           this.emailInputTarget.value.trim() &&
           this.phoneInputTarget.value.trim()
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  showError(message) {
    if (this.hasStepErrorTarget) {
      this.stepErrorTarget.textContent = message
      this.stepErrorTarget.classList.remove("hidden")
    }
  }

  clearErrors() {
    if (this.hasStepErrorTarget) {
      this.stepErrorTarget.classList.add("hidden")
    }

    // Clear input error styles
    const inputs = [
      this.firstNameInputTarget,
      this.lastNameInputTarget,
      this.emailInputTarget,
      this.phoneInputTarget
    ].filter(input => input)

    inputs.forEach(input => input.classList.remove("border-red-500"))
  }

  // ==========================================
  // Date Selection
  // ==========================================

  selectDate(event) {
    const button = event.currentTarget
    const date = button.dataset.date

    // Don't allow selection of unavailable dates
    if (button.dataset.available === "false") {
      return
    }

    // Update selected state
    this.selectedDateValue = date

    // Clear time selection when date changes
    this.selectedTimeValue = ""
    if (this.hasScheduledAtInputTarget) {
      this.scheduledAtInputTarget.value = ""
    }

    // Hide continue buttons until new time is selected
    this.hideContinueButtons()

    // Reset summary time display
    if (this.hasSummaryTimeTarget) {
      this.summaryTimeTarget.textContent = "Select a time"
    }

    // Update visual state of all date buttons
    this.dateButtonTargets.forEach(btn => {
      const isSelected = btn.dataset.date === date
      const isAvailable = btn.dataset.available === "true"

      // Toggle marker classes
      btn.classList.toggle("date-selected", isSelected)
      btn.classList.toggle("date-available", !isSelected && isAvailable)

      // Toggle style classes for selected state
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)

      // Toggle style classes for available (unselected) state
      btn.classList.toggle("bg-white", !isSelected && isAvailable)
      btn.classList.toggle("border-2", !isSelected && isAvailable)
      btn.classList.toggle("border-gray-200", !isSelected && isAvailable)
      btn.classList.toggle("text-gray-900", !isSelected && isAvailable)

      // Update the day label color
      const dayLabel = btn.querySelector("span:last-child")
      if (dayLabel) {
        dayLabel.classList.toggle("text-gray-300", isSelected)
        dayLabel.classList.toggle("text-gray-500", !isSelected)
      }
    })

    // Load slots for selected date
    this.loadSlotsForDate(date)

    // Close calendar modal if open
    this.closeCalendar()

    // Update summary
    this.updateSummary()

    // Update month label to reflect selected date's month
    this.updateMonthLabel(date)

    // Auto-scroll to show future dates (so user sees there's more to scroll)
    this.scrollToShowFutureDates(date)
  }

  /**
   * Updates the month/year label to show the month of the selected date
   */
  updateMonthLabel(dateString) {
    if (!this.hasMonthLabelTarget) return

    const date = new Date(dateString)
    const monthYear = date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric"
    })
    this.monthLabelTarget.textContent = monthYear
  }

  async loadSlotsForDate(date) {
    if (!this.hasSlotsContainerTarget) return

    // Show loading state
    this.showSlotsLoading()

    try {
      const url = new URL(this.slotsUrlValue, window.location.origin)
      url.searchParams.set("from", date)
      url.searchParams.set("to", date)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to load slots")

      const data = await response.json()
      this.renderSlots(data.slots[date] || [])

    } catch (error) {
      console.error("Error loading slots:", error)
      this.showNoSlots("Failed to load available times. Please try again.")
    }
  }

  showSlotsLoading() {
    // Just hide the no-slots message, let the animation handle the transition
    if (this.hasNoSlotsTarget) this.noSlotsTarget.classList.add("hidden")
  }

  renderSlots(slots) {
    if (!slots || slots.length === 0) {
      this.showNoSlots("No available times for this date")
      return
    }

    if (this.hasNoSlotsTarget) this.noSlotsTarget.classList.add("hidden")
    if (this.hasSlotsContainerTarget) {
      this.slotsContainerTarget.classList.remove("hidden")
      this.slotsContainerTarget.innerHTML = this.buildSlotsHTML(slots)

      // Add fade-in animation class
      this.slotsContainerTarget.classList.add("slots-fade-in")
      // Remove class after animation completes so it can be re-triggered
      setTimeout(() => {
        this.slotsContainerTarget.classList.remove("slots-fade-in")
      }, 300)
    }
  }

  buildSlotsHTML(slots) {
    return `
      <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        ${slots.map(slot => this.buildSlotButton(slot)).join("")}
      </div>
    `
  }

  buildSlotButton(slot) {
    const time = slot.time
    const datetime = slot.datetime
    const isSelected = this.selectedTimeValue === datetime
    const timeIcon = this.getTimeIcon(time)

    return `
      <button type="button"
              class="slot-button ${isSelected ? "slot-selected" : ""}
                     flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg
                     border transition-all duration-150 text-sm font-medium
                     ${isSelected
                       ? "border-transparent bg-gray-900 text-white"
                       : "border-gray-200 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"}"
              data-action="click->viewing-booking#selectTime"
              data-time="${time}"
              data-datetime="${datetime}">
        <i class="${timeIcon} text-sm"></i>
        <span>${time}</span>
      </button>
    `
  }

  getTimeIcon(time) {
    const hour = parseInt(time.split(":")[0])
    if (hour < 12) return "ph ph-sun-horizon" // Morning
    return "ph ph-sun" // Afternoon/Evening
  }

  showNoSlots(message) {
    if (this.hasSlotsContainerTarget) this.slotsContainerTarget.classList.add("hidden")
    if (this.hasSlotsLoadingTarget) this.slotsLoadingTarget.classList.add("hidden")
    if (this.hasNoSlotsTarget) {
      this.noSlotsTarget.classList.remove("hidden")
      const messageEl = this.noSlotsTarget.querySelector("[data-message]")
      if (messageEl) messageEl.textContent = message
    }
  }

  // ==========================================
  // Time Selection
  // ==========================================

  selectTime(event) {
    const button = event.currentTarget
    const datetime = button.dataset.datetime
    const time = button.dataset.time

    // Update selected state
    this.selectedTimeValue = datetime

    // Update hidden input
    if (this.hasScheduledAtInputTarget) {
      this.scheduledAtInputTarget.value = datetime
    }

    // Update visual state of all slot buttons
    this.slotsContainerTarget.querySelectorAll(".slot-button").forEach(btn => {
      const isSelected = btn.dataset.datetime === datetime
      btn.classList.toggle("slot-selected", isSelected)
      btn.classList.toggle("border-transparent", isSelected)
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("border-gray-200", !isSelected)
      btn.classList.toggle("bg-white", !isSelected)
      btn.classList.toggle("text-gray-700", !isSelected)
    })

    // Update summary
    this.updateSummary()

    // Show continue buttons now that time is selected
    this.showContinueButtons()
  }

  // ==========================================
  // Calendar Modal
  // ==========================================

  openCalendar() {
    const isMobile = window.innerWidth < 768

    if (isMobile && this.hasExpandedCalendarViewTarget) {
      // Mobile: Use inline expansion
      this.openInlineCalendar()
    } else if (this.hasCalendarModalTarget) {
      // Desktop: Use modal
      this.openCalendarModal()
    }
  }

  /**
   * Open calendar as inline expansion (mobile)
   */
  openInlineCalendar() {
    // Hide collapsed view, show expanded
    if (this.hasCollapsedDateViewTarget) {
      this.collapsedDateViewTarget.classList.add("hidden")
    }
    if (this.hasExpandedCalendarViewTarget) {
      this.expandedCalendarViewTarget.classList.remove("hidden")
    }

    // Initialize the inline calendar month
    const today = new Date()
    this.calendarMonthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`

    // Update nav button states
    this.updateInlineCalendarNavButtons()

    // Auto-select the first available date (or currently selected date) and show its slots
    const dateToShow = this.selectedDateValue || this.datesWithAvailabilityValue[0]
    if (dateToShow) {
      this.selectDateInInlineCalendar(dateToShow)
    }
  }

  /**
   * Open calendar as modal (desktop)
   */
  openCalendarModal() {
    // Initialize calendar month to current month when opening
    const currentMonth = this.calendarModalTarget.dataset.currentMonth
    if (currentMonth) {
      this.calendarMonthValue = currentMonth
    }

    this.calendarModalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    // Update nav button states
    this.updateCalendarNavButtons()

    // Auto-select the first available date (or currently selected date) and show its slots
    const dateToShow = this.selectedDateValue || this.datesWithAvailabilityValue[0]
    if (dateToShow) {
      this.selectDateInCalendar(dateToShow)
    }
  }

  /**
   * Select a date in the calendar modal (without closing it)
   * Used when opening the calendar to pre-select a date
   */
  selectDateInCalendar(date) {
    // Update the selected date value
    this.selectedDateValue = date

    // Update visual state of calendar day buttons in mobile calendar container
    if (this.hasMobileCalendarContainerTarget) {
      this.updateCalendarDayButtons(this.mobileCalendarContainerTarget, date)
    }

    // Update visual state of calendar day buttons in desktop calendar container
    if (this.hasDesktopCalendarContainerTarget) {
      this.updateCalendarDayButtons(this.desktopCalendarContainerTarget, date)
    }

    // Update the mobile date label
    if (this.hasCalendarSelectedDateLabelTarget) {
      const dateObj = new Date(date)
      const formattedDate = dateObj.toLocaleDateString("en-GB", {
        weekday: "long",
        month: "long",
        day: "numeric"
      })
      this.calendarSelectedDateLabelTarget.textContent = formattedDate
    }

    // Update the desktop date label
    if (this.hasDesktopCalendarDateLabelTarget) {
      const dateObj = new Date(date)
      const formattedDate = dateObj.toLocaleDateString("en-GB", {
        weekday: "long",
        month: "long",
        day: "numeric"
      })
      this.desktopCalendarDateLabelTarget.textContent = formattedDate
    }

    // Load and show time slots (both mobile and desktop)
    this.loadSlotsForCalendarModal(date)
  }

  /**
   * Update calendar day button styles within a container
   */
  updateCalendarDayButtons(container, selectedDate) {
    const calendarButtons = container.querySelectorAll(".calendar-day")
    calendarButtons.forEach(btn => {
      const isSelected = btn.dataset.date === selectedDate
      const hasSlots = btn.dataset.available === "true"

      // Remove all state classes
      btn.classList.remove("calendar-day-selected", "border-2", "border-gray-900", "bg-white", "font-semibold", "text-gray-900")
      btn.classList.remove("bg-gray-900", "text-white", "font-medium")

      if (isSelected) {
        // Add selected state - white circle with black border and black text
        btn.classList.add("calendar-day-selected", "border-2", "border-gray-900", "bg-white", "text-gray-900", "font-semibold")
      } else if (hasSlots) {
        // Restore available state - solid black circle with white text
        btn.classList.add("bg-gray-900", "text-white", "font-medium")
      }
    })
  }

  closeCalendar() {
    if (this.hasCalendarModalTarget) {
      this.calendarModalTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  /**
   * Unified handler for calendar date selection
   * Routes to inline or modal calendar based on which is active
   */
  selectCalendarDate(event) {
    const button = event.currentTarget
    const date = button.dataset.date
    const available = button.dataset.available === "true"

    if (!available) return

    // Check which calendar is visible
    if (this.hasExpandedCalendarViewTarget && !this.expandedCalendarViewTarget.classList.contains("hidden")) {
      // Inline calendar is active
      this.selectDateInInlineCalendar(date)
    } else if (this.hasCalendarModalTarget && !this.calendarModalTarget.classList.contains("hidden")) {
      // Modal calendar is active
      this.selectDateInCalendar(date)
    }
  }

  // ==========================================
  // Inline Calendar (Mobile)
  // ==========================================

  /**
   * Select a date in the inline calendar
   */
  selectDateInInlineCalendar(date) {
    this.selectedDateValue = date

    // Update visual state of calendar day buttons
    if (this.hasInlineCalendarContainerTarget) {
      const calendarButtons = this.inlineCalendarContainerTarget.querySelectorAll(".calendar-day")
      calendarButtons.forEach(btn => {
        const isSelected = btn.dataset.date === date
        const hasSlots = btn.dataset.available === "true"

        // Remove selected state classes
        btn.classList.remove("calendar-day-selected", "border-2", "border-gray-900", "bg-white", "font-semibold", "text-gray-900")

        if (isSelected) {
          btn.classList.add("calendar-day-selected", "border-2", "border-gray-900", "bg-white", "text-gray-900", "font-semibold")
          btn.classList.remove("bg-gray-900", "text-white")
        } else if (hasSlots) {
          btn.classList.add("bg-gray-900", "text-white", "font-medium")
        }
      })
    }

    // Update the date label
    if (this.hasInlineCalendarDateLabelTarget) {
      const dateObj = new Date(date)
      const formattedDate = dateObj.toLocaleDateString("en-GB", {
        weekday: "long",
        month: "long",
        day: "numeric"
      })
      this.inlineCalendarDateLabelTarget.textContent = formattedDate
    }

    // Load time slots
    this.loadSlotsForInlineCalendar(date)
  }

  /**
   * Handle date selection from inline calendar
   */
  selectDateFromInlineCalendar(event) {
    const button = event.currentTarget
    const date = button.dataset.date
    const available = button.dataset.available === "true"

    if (!available) return

    this.selectDateInInlineCalendar(date)
  }

  /**
   * Load time slots for inline calendar
   */
  async loadSlotsForInlineCalendar(date) {
    if (!this.hasInlineCalendarTimeSlotsContainerTarget) return


    try {
      const url = new URL(this.slotsUrlValue, window.location.origin)
      url.searchParams.set("from", date)
      url.searchParams.set("to", date)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to load slots")

      const data = await response.json()
      const slots = data.slots[date] || []

      if (slots.length === 0) {
        this.inlineCalendarTimeSlotsContainerTarget.innerHTML = `
          <div class="text-center py-4 text-gray-500">No available times for this date</div>
        `
        return
      }

      this.inlineCalendarTimeSlotsContainerTarget.innerHTML = `<div class="calendar-slots-fade-in">${this.buildInlineCalendarSlotsHTML(slots)}</div>`

    } catch (error) {
      console.error("Error loading slots:", error)
      this.inlineCalendarTimeSlotsContainerTarget.innerHTML = `
        <div class="text-center py-4 text-red-500">Failed to load times. Please try again.</div>
      `
    }
  }

  /**
   * Build HTML for inline calendar time slots
   */
  buildInlineCalendarSlotsHTML(slots) {
    return `
      <div class="grid grid-cols-3 gap-2">
        ${slots.map(slot => this.buildInlineCalendarSlotButton(slot)).join("")}
      </div>
    `
  }

  /**
   * Build a single time slot button for inline calendar
   */
  buildInlineCalendarSlotButton(slot) {
    const time = slot.time
    const datetime = slot.datetime
    const timeIcon = this.getTimeIcon(time)
    const hour = parseInt(time.split(":")[0])
    const minute = time.split(":")[1]
    const period = hour >= 12 ? "pm" : "am"
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour)
    const displayTime = `${displayHour}:${minute}${period}`

    return `
      <button type="button"
              class="flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg
                     border border-gray-200 bg-white text-gray-700
                     hover:border-gray-400 hover:bg-gray-50 transition-all duration-150 text-sm font-medium"
              data-action="click->viewing-booking#selectTimeFromInlineCalendar"
              data-time="${time}"
              data-datetime="${datetime}">
        <i class="${timeIcon} text-sm"></i>
        <span>${displayTime}</span>
      </button>
    `
  }

  /**
   * Handle time selection from inline calendar - closes and returns to collapsed view
   */
  selectTimeFromInlineCalendar(event) {
    const button = event.currentTarget
    const datetime = button.dataset.datetime

    // Update selected time
    this.selectedTimeValue = datetime

    // Update hidden input
    if (this.hasScheduledAtInputTarget) {
      this.scheduledAtInputTarget.value = datetime
    }

    // Close inline calendar and return to collapsed view
    this.closeInlineCalendar()

    // Update the collapsed view
    this.updateCollapsedViewAfterSelection()
  }

  /**
   * Close inline calendar and return to collapsed view
   */
  closeInlineCalendar() {
    if (this.hasExpandedCalendarViewTarget) {
      this.expandedCalendarViewTarget.classList.add("hidden")
    }
    if (this.hasCollapsedDateViewTarget) {
      this.collapsedDateViewTarget.classList.remove("hidden")
    }
  }

  /**
   * Update collapsed view after selecting from inline calendar
   */
  updateCollapsedViewAfterSelection() {
    // Update visual state of horizontal date picker buttons
    this.dateButtonTargets.forEach(btn => {
      const isSelected = btn.dataset.date === this.selectedDateValue
      const isAvailable = btn.dataset.available === "true"

      btn.classList.toggle("date-selected", isSelected)
      btn.classList.toggle("date-available", !isSelected && isAvailable)
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("bg-white", !isSelected && isAvailable)
      btn.classList.toggle("border-2", !isSelected && isAvailable)
      btn.classList.toggle("border-gray-200", !isSelected && isAvailable)
      btn.classList.toggle("text-gray-900", !isSelected && isAvailable)

      const dayLabel = btn.querySelector("span:last-child")
      if (dayLabel) {
        dayLabel.classList.toggle("text-gray-300", isSelected)
        dayLabel.classList.toggle("text-gray-500", !isSelected)
      }
    })

    // Load slots for selected date in main view
    this.loadSlotsForDate(this.selectedDateValue)

    // Update summary
    this.updateSummary()

    // Update month label
    this.updateMonthLabel(this.selectedDateValue)

    // Scroll to show selected date
    this.scrollToShowFutureDates(this.selectedDateValue)

    // Show continue buttons
    this.showContinueButtons()
  }

  /**
   * Navigate to previous month in inline calendar
   */
  inlineCalendarPrevMonth() {
    const current = this.getCurrentCalendarMonth()
    const minMonth = this.getMinInlineCalendarMonth()

    if (current <= minMonth) return

    const prev = new Date(current.getFullYear(), current.getMonth() - 1, 1)
    this.updateInlineCalendarMonth(prev)
    this.updateInlineCalendarNavButtons()
  }

  /**
   * Navigate to next month in inline calendar
   */
  inlineCalendarNextMonth() {
    const current = this.getCurrentCalendarMonth()
    const maxMonth = this.getMaxInlineCalendarMonth()

    if (current >= maxMonth) return

    const next = new Date(current.getFullYear(), current.getMonth() + 1, 1)
    this.updateInlineCalendarMonth(next)
    this.updateInlineCalendarNavButtons()
  }

  /**
   * Get min month for inline calendar
   */
  getMinInlineCalendarMonth() {
    if (this.hasExpandedCalendarViewTarget) {
      const inlineCalendar = this.expandedCalendarViewTarget.querySelector(".inline-calendar")
      const minMonth = inlineCalendar?.dataset.minMonth
      if (minMonth) {
        const [year, month] = minMonth.split("-").map(Number)
        return new Date(year, month - 1, 1)
      }
    }
    return new Date()
  }

  /**
   * Get max month for inline calendar
   */
  getMaxInlineCalendarMonth() {
    if (this.hasExpandedCalendarViewTarget) {
      const inlineCalendar = this.expandedCalendarViewTarget.querySelector(".inline-calendar")
      const maxMonth = inlineCalendar?.dataset.maxMonth
      if (maxMonth) {
        const [year, month] = maxMonth.split("-").map(Number)
        return new Date(year, month - 1, 1)
      }
    }
    return new Date()
  }

  /**
   * Update nav button states for inline calendar
   */
  updateInlineCalendarNavButtons() {
    const current = this.getCurrentCalendarMonth()
    const minMonth = this.getMinInlineCalendarMonth()
    const maxMonth = this.getMaxInlineCalendarMonth()

    if (this.hasInlineCalendarPrevButtonTarget) {
      this.inlineCalendarPrevButtonTarget.disabled = current <= minMonth
    }
    if (this.hasInlineCalendarNextButtonTarget) {
      this.inlineCalendarNextButtonTarget.disabled = current >= maxMonth
    }
  }

  /**
   * Update inline calendar to show a different month
   */
  async updateInlineCalendarMonth(date) {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    this.calendarMonthValue = `${year}-${String(month).padStart(2, "0")}`

    // Update the month title
    if (this.hasInlineCalendarMonthTitleTarget) {
      const monthName = date.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
      this.inlineCalendarMonthTitleTarget.textContent = monthName
    }

    // Fetch and render the new month's calendar
    if (this.hasInlineCalendarContainerTarget) {
      try {
        const url = new URL(window.location.href)
        url.searchParams.set("calendar_month", this.calendarMonthValue)
        url.searchParams.set("mobile_calendar", "true")

        const response = await fetch(url, {
          headers: {
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest"
          }
        })

        if (response.ok) {
          const html = await response.text()
          this.inlineCalendarContainerTarget.innerHTML = html
        }
      } catch (error) {
        console.error("Error loading calendar month:", error)
      }
    }
  }

  // ==========================================
  // Calendar Modal (Desktop)
  // ==========================================

  /**
   * Navigate to previous month in the mobile calendar
   */
  calendarPrevMonth() {
    const current = this.getCurrentCalendarMonth()
    const minMonth = this.getMinCalendarMonth()

    // Check if we can go back
    if (current <= minMonth) return

    const prev = new Date(current.getFullYear(), current.getMonth() - 1, 1)
    this.updateMobileCalendar(prev)
    this.updateCalendarNavButtons()
  }

  /**
   * Navigate to next month in the mobile calendar
   */
  calendarNextMonth() {
    const current = this.getCurrentCalendarMonth()
    const maxMonth = this.getMaxCalendarMonth()

    // Check if we can go forward
    if (current >= maxMonth) return

    const next = new Date(current.getFullYear(), current.getMonth() + 1, 1)
    this.updateMobileCalendar(next)
    this.updateCalendarNavButtons()
  }

  /**
   * Get the minimum month with availability
   */
  getMinCalendarMonth() {
    if (this.hasCalendarModalTarget) {
      const minMonth = this.calendarModalTarget.dataset.minMonth
      if (minMonth) {
        const [year, month] = minMonth.split("-").map(Number)
        return new Date(year, month - 1, 1)
      }
    }
    return new Date()
  }

  /**
   * Get the maximum month with availability
   */
  getMaxCalendarMonth() {
    if (this.hasCalendarModalTarget) {
      const maxMonth = this.calendarModalTarget.dataset.maxMonth
      if (maxMonth) {
        const [year, month] = maxMonth.split("-").map(Number)
        return new Date(year, month - 1, 1)
      }
    }
    return new Date()
  }

  /**
   * Update the disabled state of calendar nav buttons
   */
  updateCalendarNavButtons() {
    const current = this.getCurrentCalendarMonth()
    const minMonth = this.getMinCalendarMonth()
    const maxMonth = this.getMaxCalendarMonth()

    if (this.hasCalendarPrevButtonTarget) {
      this.calendarPrevButtonTarget.disabled = current <= minMonth
    }
    if (this.hasCalendarNextButtonTarget) {
      this.calendarNextButtonTarget.disabled = current >= maxMonth
    }
  }

  /**
   * Handle date selection from the full calendar modal (mobile)
   * Shows time slots in the calendar view - selecting a time closes the modal
   */
  selectDateFromCalendar(event) {
    const button = event.currentTarget
    const date = button.dataset.date
    const available = button.dataset.available === "true"

    if (!available) return

    this.selectDateInCalendar(date)
  }

  /**
   * Load time slots for the calendar modal view (both mobile and desktop)
   */
  async loadSlotsForCalendarModal(date) {
    const hasMobileContainer = this.hasCalendarTimeSlotsContainerTarget
    const hasDesktopContainer = this.hasDesktopCalendarTimeSlotsContainerTarget

    if (!hasMobileContainer && !hasDesktopContainer) return


    try {
      const url = new URL(this.slotsUrlValue, window.location.origin)
      url.searchParams.set("from", date)
      url.searchParams.set("to", date)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to load slots")

      const data = await response.json()
      const slots = data.slots[date] || []

      if (slots.length === 0) {
        const noSlotsHtml = `<div class="text-center py-4 text-gray-500">No available times for this date</div>`
        if (hasMobileContainer) {
          this.calendarTimeSlotsContainerTarget.innerHTML = noSlotsHtml
        }
        if (hasDesktopContainer) {
          this.desktopCalendarTimeSlotsContainerTarget.innerHTML = noSlotsHtml
        }
        return
      }

      // Render time slots with fade-in animation
      const slotsHtml = `<div class="calendar-slots-fade-in">${this.buildCalendarSlotsHTML(slots)}</div>`
      if (hasMobileContainer) {
        this.calendarTimeSlotsContainerTarget.innerHTML = slotsHtml
      }
      if (hasDesktopContainer) {
        this.desktopCalendarTimeSlotsContainerTarget.innerHTML = slotsHtml
      }

    } catch (error) {
      console.error("Error loading slots:", error)
      const errorHtml = `<div class="text-center py-4 text-red-500">Failed to load times. Please try again.</div>`
      if (hasMobileContainer) {
        this.calendarTimeSlotsContainerTarget.innerHTML = errorHtml
      }
      if (hasDesktopContainer) {
        this.desktopCalendarTimeSlotsContainerTarget.innerHTML = errorHtml
      }
    }
  }

  /**
   * Build HTML for time slots in the calendar modal
   */
  buildCalendarSlotsHTML(slots) {
    return `
      <div class="grid grid-cols-3 gap-2">
        ${slots.map(slot => this.buildCalendarSlotButton(slot)).join("")}
      </div>
    `
  }

  /**
   * Build a single time slot button for the calendar modal
   */
  buildCalendarSlotButton(slot) {
    const time = slot.time
    const datetime = slot.datetime
    const timeIcon = this.getTimeIcon(time)
    // Convert 24h to 12h format
    const hour = parseInt(time.split(":")[0])
    const minute = time.split(":")[1]
    const period = hour >= 12 ? "pm" : "am"
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour)
    const displayTime = `${displayHour}:${minute}${period}`

    return `
      <button type="button"
              class="flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg
                     border border-gray-200 bg-white text-gray-700
                     hover:border-gray-400 hover:bg-gray-50 transition-all duration-150 text-sm font-medium"
              data-action="click->viewing-booking#selectTimeFromCalendar"
              data-time="${time}"
              data-datetime="${datetime}">
        <i class="${timeIcon} text-sm"></i>
        <span>${displayTime}</span>
      </button>
    `
  }

  /**
   * Handle time selection from the calendar modal
   * This closes the modal and returns to the collapsed view
   */
  selectTimeFromCalendar(event) {
    const button = event.currentTarget
    const datetime = button.dataset.datetime
    const time = button.dataset.time

    // Update selected time
    this.selectedTimeValue = datetime

    // Update hidden input
    if (this.hasScheduledAtInputTarget) {
      this.scheduledAtInputTarget.value = datetime
    }

    // Update visual state of horizontal date picker buttons
    this.dateButtonTargets.forEach(btn => {
      const isSelected = btn.dataset.date === this.selectedDateValue
      const isAvailable = btn.dataset.available === "true"

      btn.classList.toggle("date-selected", isSelected)
      btn.classList.toggle("date-available", !isSelected && isAvailable)
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("bg-white", !isSelected && isAvailable)
      btn.classList.toggle("border-2", !isSelected && isAvailable)
      btn.classList.toggle("border-gray-200", !isSelected && isAvailable)
      btn.classList.toggle("text-gray-900", !isSelected && isAvailable)

      const dayLabel = btn.querySelector("span:last-child")
      if (dayLabel) {
        dayLabel.classList.toggle("text-gray-300", isSelected)
        dayLabel.classList.toggle("text-gray-500", !isSelected)
      }
    })

    // Load slots for selected date in the main view
    this.loadSlotsForDate(this.selectedDateValue)

    // Close the calendar modal
    this.closeCalendar()

    // Update summary
    this.updateSummary()

    // Update month label
    this.updateMonthLabel(this.selectedDateValue)

    // Scroll to show the selected date in the horizontal picker
    this.scrollToShowFutureDates(this.selectedDateValue)

    // Show continue buttons
    this.showContinueButtons()
  }

  /**
   * Get the current calendar month being displayed
   */
  getCurrentCalendarMonth() {
    if (this.calendarMonthValue) {
      const [year, month] = this.calendarMonthValue.split("-").map(Number)
      return new Date(year, month - 1, 1)
    }
    // Default to current month
    return new Date()
  }

  /**
   * Update the mobile calendar to show a different month
   */
  async updateMobileCalendar(date) {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    this.calendarMonthValue = `${year}-${String(month).padStart(2, "0")}`

    // Update the month title
    if (this.hasCalendarMonthTitleTarget) {
      const monthName = date.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
      this.calendarMonthTitleTarget.textContent = monthName
    }

    // Fetch and render the new month's calendar
    if (this.hasMobileCalendarContainerTarget) {
      try {
        const url = new URL(window.location.href)
        url.searchParams.set("calendar_month", this.calendarMonthValue)
        url.searchParams.set("mobile_calendar", "true")

        const response = await fetch(url, {
          headers: {
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest"
          }
        })

        if (response.ok) {
          const html = await response.text()
          this.mobileCalendarContainerTarget.innerHTML = html
        }
      } catch (error) {
        console.error("Error loading calendar month:", error)
      }
    }
  }

  // ==========================================
  // Summary Updates
  // ==========================================

  updateSummary() {
    // Format date for display
    let formattedDate = "Select a date"
    let formattedTime = "Select a time"

    if (this.selectedDateValue) {
      const date = new Date(this.selectedDateValue)
      formattedDate = date.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long"
      })
    }

    if (this.selectedTimeValue) {
      const datetime = new Date(this.selectedTimeValue)
      formattedTime = datetime.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit"
      })
    }

    // Update desktop summary panel
    if (this.hasSummaryDateTarget) {
      this.summaryDateTarget.textContent = formattedDate
    }
    if (this.hasSummaryTimeTarget) {
      this.summaryTimeTarget.textContent = formattedTime
    }

    // Update mobile bottom bar
    if (this.hasMobileBarDateTarget) {
      this.mobileBarDateTarget.textContent = formattedDate
    }
    if (this.hasMobileBarTimeTarget) {
      this.mobileBarTimeTarget.textContent = formattedTime
    }
  }

  // ==========================================
  // Form Actions
  // ==========================================

  continueClick() {
    if (this.currentStepValue < 3) {
      this.nextStep()
    } else {
      // Prevent double submission
      if (this._submitting) return
      this._submitting = true

      // Disable all submit buttons to give visual feedback
      if (this.hasContinueButtonTarget) {
        this.continueButtonTarget.disabled = true
      }
      if (this.hasMobileBarButtonTarget) {
        this.mobileBarButtonTarget.disabled = true
      }

      // Submit form
      if (this.hasFormTarget) {
        this.formTarget.submit()
      }
    }
  }

  // Input change handlers - save details to sessionStorage
  inputChanged() {
    this.saveViewerDetails()
  }

  // ==========================================
  // Navigation helpers
  // ==========================================

  jumpToNextAvailableDate() {
    // Find next date with availability after current selection
    const currentIndex = this.datesWithAvailabilityValue.indexOf(this.selectedDateValue)
    const nextDate = this.datesWithAvailabilityValue[currentIndex + 1]

    if (nextDate) {
      this.selectedDateValue = nextDate
      this.loadSlotsForDate(nextDate)
      this.scrollDateIntoView(nextDate)
    }
  }

  scrollDateIntoView(date) {
    const button = this.dateButtonTargets.find(btn => btn.dataset.date === date)
    if (button && this.hasDateScrollerTarget) {
      button.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" })
    }
  }

  /**
   * Scrolls the date picker so that the selected date is visible with one date's worth of padding on the left.
   */
  scrollToShowFutureDates(date) {
    const button = this.dateButtonTargets.find(btn => btn.dataset.date === date)
    if (button && this.hasDateScrollerTarget) {
      const scroller = this.dateScrollerTarget

      // Get button position relative to scroller's scrollable content
      const scrollerRect = scroller.getBoundingClientRect()
      const buttonRect = button.getBoundingClientRect()
      const currentScroll = scroller.scrollLeft
      const buttonOffset = buttonRect.left - scrollerRect.left + currentScroll

      // Scroll so there's some padding on the left (~20px)
      const targetScroll = Math.max(0, buttonOffset - 20)
      scroller.scrollTo({ left: targetScroll, behavior: "smooth" })
    }
  }

  scrollToToday() {
    const today = new Date().toISOString().split("T")[0]
    this.scrollDateIntoView(today)
  }
}
